import { ColumnSort, PaginationState } from '@tanstack/react-table';
import { useSearchParams, useLocation } from 'react-router-dom';

import { track } from '@adsk/acs-web-analytics';
import { EntitlementsProject } from '@adsk/alloy-provider';

import {
  SORT_DIRECTION,
  SIDE_FILTERS_KEYS,
  DEFAULT_SORT_FIELD,
  AnalyticsEvents,
  DEFAULT_LIMIT,
} from '../constants';
import { isValidFilter } from '../helpers/filters';

export type SideFiltersType = Record<string, string>;

function filtersCount(f: SideFiltersType) {
  return Object.keys(f)?.length;
}

function countFiltersForAnalytics(f: SideFiltersType) {
  return Object.keys(f)?.reduce((acc, key) => {
    const value = f[key];
    const count = value.split(',').length; // support multiple values
    return acc + count;
  }, 0);
}

function getFiltersFromUrl(restUrlParams: SideFiltersType) {
  const sideFilters: SideFiltersType = {};
  for (const [filterKey, filterValue] of Object.entries(restUrlParams)) {
    if (
      SIDE_FILTERS_KEYS.includes(filterKey) &&
      isValidFilter(filterKey, filterValue)
    ) {
      // filter only query params that relevant to side filters
      sideFilters[filterKey] = filterValue;
    }
  }
  return { sideFilters };
}

function getSortFromUrl(sortKeyFromUrl: string, sortValueFromUrl: string) {
  const sortKey =
    (sortKeyFromUrl as keyof EntitlementsProject) || DEFAULT_SORT_FIELD;
  const sortValue =
    sortValueFromUrl === 'asc'
      ? SORT_DIRECTION.ASCENDING
      : SORT_DIRECTION.DESCENDING; // if not ascending or query param not exist

  return { sortKey, sortValue };
}

type UseFiltersProps = {
  onPaginationChange?: (pagination: PaginationState) => void;
};

export const useUrlParams = () => {
  const location = useLocation();
  return useSearchParams(location.search);
};

export const useFilters = ({ onPaginationChange }: UseFiltersProps = {}) => {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams(location.search);
  const {
    search = '',
    pageIndex,
    sortKey: sortKeyFromUrl,
    sortValue: sortValueFromUrl,
    ...restUrlParams
  } = Object.fromEntries(searchParams);
  const { sortKey, sortValue } = getSortFromUrl(
    sortKeyFromUrl,
    sortValueFromUrl,
  );
  const { sideFilters } = getFiltersFromUrl(restUrlParams);

  function resetSideFilters() {
    track(AnalyticsEvents.clearFilters);
    setSearchParams(
      (prevParams) => {
        SIDE_FILTERS_KEYS.forEach((key) => prevParams.delete(key));
        prevParams.set('pageIndex', '0');
        return prevParams;
      },
      { replace: true },
    );
  }

  return {
    search,
    pageIndex: Number(pageIndex) || 0,
    filters: sideFilters,

    sorting: { sortKey, sortDirection: sortValue },
    pagination: {
      pageIndex: Number(pageIndex) || 0,
    },
    onSearchChange: (searchedValue: string) =>
      setSearchParams(
        (prevParams) => {
          prevParams.set('search', searchedValue);
          prevParams.set('pageIndex', '0');
          return prevParams;
        },
        { replace: true },
      ),
    tableProps: {
      pagination: {
        pageSize: DEFAULT_LIMIT,
        pageIndex: Number(pageIndex) || 0,
      } satisfies PaginationState,
      sorting: [
        {
          id: sortKey,
          desc: sortValue === SORT_DIRECTION.DESCENDING,
        },
      ] satisfies ColumnSort[],
      onPaginationChange: ({ pageIndex, pageSize }: PaginationState) => {
        onPaginationChange?.({ pageIndex, pageSize });
        setSearchParams(
          (prevParams) => {
            prevParams.set('pageIndex', pageIndex.toString());
            return prevParams;
          },
          { replace: true },
        );
      },
      onSortingChange: ([{ id, desc }]: ColumnSort[]) =>
        setSearchParams(
          (prevParams) => {
            prevParams.set('sortKey', id);
            prevParams.set(
              'sortValue',
              desc ? SORT_DIRECTION.DESCENDING : SORT_DIRECTION.ASCENDING,
            );
            return prevParams;
          },
          { replace: true },
        ),
    },
    sideFiltersProps: {
      filtersCount: filtersCount(sideFilters),
      clearFilters: resetSideFilters,
      onFilterChange: (name: string, value?: string) => {
        let analyticFields: { [key: string]: string | number | undefined } = {};
        let analyticType: string;
        const newFilters = { ...sideFilters };
        if (!value) {
          delete newFilters[name];

          analyticType = AnalyticsEvents.removeFilter;
          const analyticFiltersNumberAfterAction =
            countFiltersForAnalytics(newFilters);
          analyticFields = {
            filtered_items_no_after_action: analyticFiltersNumberAfterAction,
            removed_item_name: sideFilters[name],
          };
        } else {
          newFilters[name] = value;

          analyticType = AnalyticsEvents.addFilter;
          analyticFields = { added_item_name: value };
        }

        const analyticFiltersNumberBeforeAction =
          countFiltersForAnalytics(sideFilters);
        analyticFields = {
          ...analyticFields,
          filtered_items_no: analyticFiltersNumberBeforeAction,
        };

        track(analyticType, {
          filter_type: name,
          ...analyticFields,
        });

        setSearchParams(
          (prevParams) => {
            if (value) {
              prevParams.set(name, value);
            } else {
              prevParams.delete(name);
            }
            return prevParams;
          },
          { replace: true },
        );
      },
    },
  };
};
